import React from "react";

const TermsAndConditionsScreen = () => {
  return (
    <section className="mx-auto w-full max-w-4xl">
      <article className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
        <h1 className="mb-2 text-3xl font-semibold text-slate-900">Terms &amp; Conditions</h1>
        <p className="mb-2 text-sm text-slate-500">Last updated: February 23, 2026</p>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">1. Scope</h4>
        <p className="text-sm leading-6 text-slate-700">
          These terms govern use of the Practidex marketplace web application, including buyer,
          creator, and admin account functionality, digital product discovery, checkout, and
          post-purchase access.
        </p>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">2. Accounts and Access</h4>
        <ul className="list-disc space-y-1 pl-5 text-sm leading-6 text-slate-700">
          <li>You must provide accurate registration details and keep credentials confidential.</li>
          <li>You are responsible for all activity performed through your account.</li>
          <li>We may suspend or restrict accounts involved in abuse, fraud, or policy violations.</li>
          <li>Closed accounts may be reactivated by logging in, unless permanently terminated.</li>
        </ul>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">3. Marketplace Roles</h4>
        <p className="text-sm leading-6 text-slate-700">
          Buyers can purchase and download licensed resources. Creators can publish resources and
          receive payouts once onboarding requirements are completed. Platform/admin accounts may
          moderate listings and operate financial controls.
        </p>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">4. Product Listings and Visibility</h4>
        <ul className="list-disc space-y-1 pl-5 text-sm leading-6 text-slate-700">
          <li>Creators are responsible for listing accuracy, ownership rights, and lawful content.</li>
          <li>Products can be hidden by creators and may be hidden by platform policy controls.</li>
          <li>Unonboarded creator products may remain non-public until payout requirements are met.</li>
        </ul>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">5. Pricing, Free Products, and Checkout</h4>
        <ul className="list-disc space-y-1 pl-5 text-sm leading-6 text-slate-700">
          <li>Displayed prices may be converted by currency preference for UI display.</li>
          <li>Charge calculations are determined server-side from canonical product pricing data.</li>
          <li>Free products may be subject to anti-abuse controls and cooldown limits.</li>
          <li>Mixed carts grant access only after successful payment of paid items.</li>
        </ul>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">6. Payments and Payouts</h4>
        <p className="text-sm leading-6 text-slate-700">
          Stripe is the active checkout rail in the current frontend. Payment and payout states are
          reconciled using internal records and webhook-driven workflows. We may delay, hold, or
          retry transfers when risk or operational failures are detected.
        </p>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">7. Licenses and Downloads</h4>
        <ul className="list-disc space-y-1 pl-5 text-sm leading-6 text-slate-700">
          <li>Purchases grant usage rights under the applicable license count and terms.</li>
          <li>License consumption can occur on download actions per product file policy.</li>
          <li>Redistribution, resale, and unauthorized sharing are prohibited.</li>
        </ul>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">8. Refunds, Disputes, and Enforcement</h4>
        <p className="text-sm leading-6 text-slate-700">
          Refund and dispute handling may be processed through platform workflows and payment
          provider mechanisms. We reserve the right to investigate suspicious behavior and enforce
          corrective actions including order reversal and account restrictions.
        </p>
        <p className="mt-3 text-sm leading-6 text-slate-700">
          If you need help with an order or want to raise a purchase concern, contact{" "}
          <a
            href="mailto:contact@practidex.com"
            className="font-semibold text-sky-700 no-underline hover:text-sky-800"
          >
            contact@practidex.com
          </a>{" "}
          and include your account email plus the relevant order reference.
        </p>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">9. Acceptable Use</h4>
        <ul className="list-disc space-y-1 pl-5 text-sm leading-6 text-slate-700">
          <li>No fraud, scraping abuse, malware distribution, or circumvention of platform controls.</li>
          <li>No manipulation of rating/review systems or payment flows.</li>
          <li>No upload of content that infringes intellectual property or applicable law.</li>
        </ul>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">10. Intellectual Property</h4>
        <p className="text-sm leading-6 text-slate-700">
          Platform code, branding, and non-user-generated assets remain platform property.
          Creators retain ownership of their uploaded content, granting the platform rights needed
          to host, index, and distribute according to marketplace operations.
        </p>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">11. Liability and Availability</h4>
        <p className="text-sm leading-6 text-slate-700">
          Services are provided on an "as available" basis. We do not guarantee uninterrupted
          availability and are not liable for indirect or consequential losses arising from outages,
          third-party provider issues, or unauthorized account access caused by user-side compromise.
        </p>

        <h4 className="mb-2 mt-5 text-lg font-semibold text-slate-900">12. Changes to Terms</h4>
        <p className="text-sm leading-6 text-slate-700">
          We may update these terms to reflect product, legal, or operational changes. Continued
          use after updates constitutes acceptance.
        </p>
      </article>
    </section>
  );
};

export default TermsAndConditionsScreen;
